// @flow
import React, {useEffect, useState} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import Box from 'sail/Box';
import {ButtonLink} from 'sail/Button';
import Group from 'sail/Group';
import {Heading} from 'sail/Text';
import Icon from 'sail/Icon';
import Spinner from 'sail/Spinner';
import Text from 'sail/Text/Text';

import tinycolor from 'tinycolor2';

import {loadStripe} from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {useApi} from 'docs/data';

import {useCondition, useData} from 'docs/data/integration-builder';

import './PaymentsDemo.scss';

const messages = defineMessages({
  emailLabel: {
    id: 'docsApp.integrationBuilder.demo.paymentElement.emailLabel',
    description: 'Email input label',
    defaultMessage: 'Email',
  },
  paymentSucceeded: {
    id: 'docsApp.integrationBuilder.demo.paymentElement.paymentSucceeded',
    description: 'Payment succeeded message',
    defaultMessage: 'Payment succeeded!',
  },
  paymentProcessing: {
    id: 'docsApp.integrationBuilder.demo.paymentElement.paymentProcessing',
    description: 'Payment processing message',
    defaultMessage: 'Your payment is processing.',
  },
  paymentFailed: {
    id: 'docsApp.integrationBuilder.demo.paymentElement.paymentFailed',
    description: 'Payment failed message',
    defaultMessage: 'Your payment was not successful, please try again.',
  },
  unexpectedError: {
    id: 'docsApp.integrationBuilder.demo.paymentElement.unexpectedError',
    description: 'Unexpected error message',
    defaultMessage: "An error occurred. We're looking into it!",
  },
  payNow: {
    id: 'docsApp.integrationBuilder.demo.paymentElement.payNow',
    description: 'Pay now button label',
    defaultMessage: 'Pay now',
  },
  tryAgain: {
    id: 'docsApp.integrationBuilder.demo.paymentElement.tryAgain',
    description: 'Label for Try Again button',
    defaultMessage: 'Try again',
  },
});

const UNEXPECTED_ERROR_MESSAGE = messages.unexpectedError;
// payments-web-bots+payment-intents@stripe.com test account
const PAYWEB_PAYMENT_INTENTS_TEST_PUBLIC_KEY =
  'pk_test_Sds05L2renhqjHCunKB9i5i2';

const useAppearanceStyles = (hasEmail) => {
  const {appearance} = useData();
  const {theme, backgroundColor, primaryColor, textColor} = appearance;

  const getBorder = (color) => {
    if (hasEmail) {
      return theme === 'flat' ? '0' : `1px solid ${color}`;
    } else {
      return '0';
    }
  };

  // Calculations
  const background = tinycolor(backgroundColor);
  const primary = tinycolor(primaryColor);
  const contrastColor = primary.isDark() ? 'white' : 'black';
  const inputFocusBorderColor = primary
    .clone()
    .setAlpha(theme === 'stripe' ? 0.5 : 0.4);

  let inputBoxShadow = '';
  let inputFocusBoxShadow = '';
  let inputBorderColor = '';
  if (theme === 'stripe') {
    inputBoxShadow =
      '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02)';
    inputFocusBoxShadow = `0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02), 0 0 0 3px ${primary
      .clone()
      .setAlpha(0.2)}, 0 1px 1px 0 rgba(0, 0, 0, 0.08)`;
    inputBorderColor = background.isDark()
      ? background.brighten(10)
      : background.darken(10);
  } else if (theme === 'night') {
    inputBoxShadow =
      '0px 2px 4px rgb(0, 0, 0, 0.5), 0px 1px 6px rgb(0, 0, 0, .25)';
    inputFocusBoxShadow = `0px 2px 4px rgba(0, 0, 0, 0.5), 0px 1px 6px rgba(0, 0, 0, 0.25), 0 0 0 3px ${primary
      .clone()
      .setAlpha(0.2)}, 0 1px 1px 0 rgba(0, 0, 0, 0.08)`;
    inputBorderColor = background.isDark()
      ? background.brighten(7)
      : background.darken(7);
  } else if (theme === 'flat') {
    inputFocusBoxShadow = `0 0 0 3px ${primary
      .clone()
      .setAlpha(0.2)}, 0 0 0 1px ${primary.clone().setAlpha(0.5)}`;
  }

  // Styles
  const labelStyle = {
    color: textColor,
  };

  const emailStyle = {
    '--borderFocused': getBorder(inputFocusBorderColor.toHslString()),
    '--boxShadowFocused': inputFocusBoxShadow,
    backgroundColor,
    borderRadius: theme === 'flat' ? '12px' : '5px',
    boxShadow: inputBoxShadow,
    color: textColor,
    padding: hasEmail ? '12px' : '0',
    border: getBorder(inputBorderColor.toString()),
    height: hasEmail ? '44px' : '0',
    transform: hasEmail ? 'none' : 'translateY(20px) scale(0.9)',
    opacity: hasEmail ? 1 : 0,
    position: hasEmail ? 'inherit' : 'absolute',
  };

  const buttonStyle = {
    background: primaryColor,
    borderRadius: theme === 'flat' ? '12px' : '5px',
    color: contrastColor,
  };

  const spinnerStyle = {
    '--primaryColor': primaryColor,
    color: contrastColor,
  };

  return {labelStyle, emailStyle, buttonStyle, spinnerStyle};
};

const InnerPaymentElementDemo = ({
  isElementLoading,
  setError,
  setIsElementLoading,
}) => {
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {toggles} = useCondition();
  const stripe = useStripe();
  const elements = useElements();

  const intl = useIntl();
  const localizedErrorMessage = intl.formatMessage(UNEXPECTED_ERROR_MESSAGE);

  useEffect(() => {
    let isEffectStale = false;

    if (!elements) {
      return;
    }

    if (isEffectStale) {
      return;
    }

    const element = elements.getElement(PaymentElement);

    element.on('ready', () => {
      if (isEffectStale) {
        return;
      }
      setIsElementLoading(false);
    });

    element.on('loaderror', (e) => {
      if (isEffectStale) {
        return;
      }

      setError(localizedErrorMessage);
      setIsElementLoading(false);
    });

    return () => {
      isEffectStale = true;
    };
  }, [elements, localizedErrorMessage, setError, setIsElementLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const {error} = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message);
    } else {
      setMessage(localizedErrorMessage);
    }

    setIsLoading(false);
  };

  const hasEmail = toggles.email;
  const {
    labelStyle,
    emailStyle,
    buttonStyle,
    spinnerStyle,
  } = useAppearanceStyles(hasEmail);

  return (
    <Box style={{display: isElementLoading ? 'none' : 'block', width: '100%'}}>
      {hasEmail && (
        <label className="email--label" htmlFor="email" style={labelStyle}>
          <FormattedMessage {...messages.emailLabel} />
        </label>
      )}
      <input
        className="email--input"
        id="email"
        type="text"
        style={emailStyle}
      />
      <form onSubmit={handleSubmit}>
        <PaymentElement />
        <button
          className="submit"
          type="submit"
          disabled={isLoading || !stripe}
          style={buttonStyle}
        >
          <span id="button-text">
            {isLoading ? (
              <div className="spinner" id="spinner" style={spinnerStyle} />
            ) : (
              <FormattedMessage {...messages.payNow} />
            )}
          </span>
        </button>
        {message && (
          <div className="message" role="alert">
            {message}
          </div>
        )}
      </form>
    </Box>
  );
};

export const PaymentsDemo = () => {
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');
  const [isElementLoading, setIsElementLoading] = useState(true);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [messageIcon, setMessageIcon] = useState('check');
  const {api} = useApi();
  const {appearance: appearanceData} = useData();
  const intl = useIntl();

  // Since Payment Element is still in beta, will use a hardcoded key for now.
  const publishableKey = PAYWEB_PAYMENT_INTENTS_TEST_PUBLIC_KEY;
  const stripePromise = loadStripe(publishableKey);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const redirectStatus = query.get('redirect_status');
    if (redirectStatus === 'succeeded') {
      setPaymentMessage(intl.formatMessage(messages.paymentSucceeded));
      setMessageIcon('check');
    } else if (redirectStatus === 'pending') {
      setPaymentMessage(intl.formatMessage(messages.paymentProcessing));
      setMessageIcon('change');
    } else if (redirectStatus === 'failed') {
      setPaymentMessage(intl.formatMessage(messages.paymentFailed));
      setMessageIcon('block');
    }
  }, [intl]);

  useEffect(() => {
    api
      .createPaymentIntent({
        payment_method_types: [
          'giropay',
          'eps',
          'sofort',
          'p24',
          'sepa_debit',
          'card',
          'bancontact',
          'ideal',
        ],
        is_payment_element: true,
        payment_element_currency: 'eur',
      })
      .then((response) => {
        if (!response.clientSecret) {
          const domain = window.location.hostname || 'stripe.com';
          const errorMessage = domain.match('dev.stripe.me')
            ? 'This demo does not work locally.'
            : intl.formatMessage(UNEXPECTED_ERROR_MESSAGE);
          setError(errorMessage);
          return;
        }
        setClientSecret(response.clientSecret);
      });
  }, [api, intl]);

  const appearance = {
    theme: appearanceData.theme,
    variables: {
      colorPrimary: appearanceData.primaryColor,
      colorBackground: appearanceData.backgroundColor,
      colorText: appearanceData.textColor,
    },
  };
  const options = {
    clientSecret,
    appearance,
  };

  const background = tinycolor(appearanceData.backgroundColor);
  const backgroundColor = background.isDark()
    ? background.darken(20)
    : background.brighten(20);

  return (
    <Box
      className="PaymentsDemo"
      margin={{bottom: 32}}
      style={{borderRadius: '4px', backgroundColor: backgroundColor.toString()}}
    >
      <Box
        padding={12}
        flex={{alignItems: 'center'}}
        margin={{vertical: 16, horizontal: 32}}
        style={{minHeight: '384px', width: '490px'}}
      >
        {paymentMessage ? (
          <Result
            message={paymentMessage}
            setMessage={setPaymentMessage}
            icon={messageIcon}
          />
        ) : (
          <>
            {(isElementLoading || error) && <ErrorLoadingBox error={error} />}
            {clientSecret && (
              <Elements options={options} stripe={stripePromise}>
                <InnerPaymentElementDemo
                  isElementLoading={isElementLoading}
                  setIsElementLoading={setIsElementLoading}
                  setError={setError}
                />
              </Elements>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const Result = ({message, setMessage, icon}) => {
  const intl = useIntl();

  const onClickTryAgain = () => {
    // Clear query parameters from payment
    window.history.replaceState(null, null, window.location.pathname);
    setMessage(null);
  };

  return (
    message && (
      <Box
        flex={{
          direction: 'column',
          alignItems: 'center',
        }}
        style={{width: '100%'}}
      >
        <Icon icon={icon} color="gray700" style={{marginBottom: '12px'}} />
        <Heading color="gray700">{message}</Heading>
        <ButtonLink
          label={intl.formatMessage(messages.tryAgain)}
          style={{margin: 0, marginTop: '12px'}}
          onClick={onClickTryAgain}
        />
      </Box>
    )
  );
};

const ErrorLoadingBox = ({error}) => (
  <Box
    flex={{alignItems: 'center', justifyContent: 'center'}}
    style={{minHeight: '360px', width: '100%'}}
  >
    {error ? (
      <Group alignItems="center" direction="horizontal" spacing={8}>
        <Icon icon="warning" color="gray" size={14} />
        <Text size={15} color="gray600">
          {error}
        </Text>
      </Group>
    ) : (
      <Spinner size="large" />
    )}
  </Box>
);
